import type { QuoteDocument } from 'paintscout'

export const QUOTE_RECOVERY_KEY = 'QuoteEdit-recovery'

// Debounce window for the two localStorage writes below. Coalesces a burst of quote mutations /
// keystrokes into at most one full serialization+write per quiet window, removing the per-action
// main-thread stall that made the editor feel sluggish. Recovery data is best-effort (read only on
// crash/reload), so a short delay is safe. See #21233.
export const RECOVERY_WRITE_DEBOUNCE_MS = 750

const QUOTE_LOG_KEY = 'quoteLog'
const QUOTE_LOG_MAX_ENTRIES = 5

export function getQuoteRecoveryKey(id: string) {
  // save under <KEY>.id if it's a saved quote, or <KEY>.new if it's a new, unsaved quote
  return `${QUOTE_RECOVERY_KEY}.${id}`
}

interface WriteErrorHandler {
  onError?: (error: unknown) => void
}

/**
 * Persists the current unsaved quote to localStorage for crash/reload recovery. Extracted from
 * EditableQuote so it can be called through a debounce and unit-tested with a mocked localStorage.
 * The payload shape ({ quote, isNew, shouldSaveContact, time }) and key (getQuoteRecoveryKey) are
 * the contract consumed by EditQuote.tsx and CheckQuoteRecovery — do not change them.
 */
export function writeQuoteRecovery(
  { quote, isNew, shouldSaveContact }: { quote: QuoteDocument; isNew?: boolean; shouldSaveContact?: boolean },
  { onError }: WriteErrorHandler = {}
): void {
  try {
    localStorage.setItem(
      getQuoteRecoveryKey(quote._id),
      JSON.stringify({
        quote,
        isNew,
        shouldSaveContact,
        time: Date.now()
      })
    )
  } catch (error) {
    onError?.(error)
  }
}

/**
 * Maintains the `quoteLog` recovery map (keyed by quote _id, capped at the 5 most-recently-updated
 * entries) that helps recover "lost" offline quotes. Behavior-identical to the prior inline effect;
 * extracted for debouncing + testing. The key ('quoteLog') and entry shape are read by
 * CheckQuoteRecovery — preserve them.
 */
export function updateQuoteLog(
  {
    dirtyQuote,
    cleanQuote,
    isNew,
    online
  }: {
    dirtyQuote?: QuoteDocument | null
    cleanQuote: QuoteDocument
    isNew?: boolean
    online?: boolean
  },
  { onError }: WriteErrorHandler = {}
): void {
  const quoteLog: Record<string, any> = JSON.parse(localStorage.getItem(QUOTE_LOG_KEY) || '{}')

  if (dirtyQuote) {
    quoteLog[dirtyQuote._id] = {
      isNew: !!isNew,
      createdOffline: isNew ? !online : null,
      userOffline: !online,
      quote: dirtyQuote
    }
    const keys = Object.keys(quoteLog)
    if (keys.length > QUOTE_LOG_MAX_ENTRIES) {
      const keysToKeep = keys
        // Null-safe: legacy entries written by older code may lack quote.dates — treat as oldest
        // rather than throwing on the comparator.
        .sort((a, b) =>
          (quoteLog[a]?.quote?.dates?.updated ?? 0) > (quoteLog[b]?.quote?.dates?.updated ?? 0) ? -1 : 1
        )
        .slice(0, QUOTE_LOG_MAX_ENTRIES)

      keys.forEach((key) => {
        if (!keysToKeep.includes(key)) {
          delete quoteLog[key]
        }
      })
    }
  } else if (quoteLog[cleanQuote._id]) {
    quoteLog[cleanQuote._id] = {
      ...quoteLog[cleanQuote._id],
      userOffline: !online,
      saved: true
    }
  }

  try {
    localStorage.setItem(QUOTE_LOG_KEY, JSON.stringify(quoteLog))
  } catch (error) {
    onError?.(error)
  }
}
